import React, { Fragment, useState } from 'react';
import { FormGroup, Label as ReactstrapLabel, Input } from 'reactstrap';
import { get, } from 'lodash';
import Select, { createFilter } from 'react-select';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import { setHours, setMinutes, getHours, getMinutes } from 'date-fns';

import texts from '../shared/texts';

const validationText = (documentName, fieldName, key) => {
  return (
    get(texts.validations[documentName], `${fieldName}.${key}`)
    || get(texts.validations.general, key)
  );
}

export default function Field (props) {
  const { documentName, type, name: fieldName, label, value, setValue, options = [], validationErrors = [], readOnly = _ => false, shouldHide, placeholder, rows, labelHint, hint, inputProps, validations = [], hiddenRequiredLabel = false, ...extraProps } = props;
  const [hasStarted, setHasStarted] = useState(false);
  const setValueAndStart = (value) => {
    setValue(value);
    setHasStarted(true);
  };
  const isValid = validationErrors.length === 0;
  const validationCss = hasStarted && classnames({ 'is-valid': isValid, 'is-invalid': isValid === false });
  const Label = (props) => {
    return (
      <ReactstrapLabel {...props}>
        {props.children}
        {!hiddenRequiredLabel && validations.required != null && <span className="text-danger small">【必須】</span>}
        <div>
          {labelHint}
        </div>
      </ReactstrapLabel>
    )
  };
  if(shouldHide) return null;

  return (
    <FormGroup key={fieldName} {...extraProps}>
      {
        ({
          string: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input name={fieldName} value={value || ''} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} {...inputProps} />
            </Fragment>
          ),
          password: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input type="password" name={fieldName} value={value || ''} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} {...inputProps} />
            </Fragment>
          ),
          integer: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input name={fieldName} type="number" step="1" value={value != null ? value : ''} onChange={_ => setValueAndStart(_.target.value === '' ? null : parseInt(_.target.value))} className={classnames('text-right', validationCss)} readOnly={readOnly(value, props)} placeholder={placeholder} {...inputProps} />
            </Fragment>
          ),
          boolean: () => (
            <Fragment>
              <FormGroup check>
                <Label check>
                  <Input name={fieldName} type="checkbox" checked={value} onChange={_ => setValueAndStart(_.target.checked)} className={validationCss} disabled={readOnly(value, props)} {...inputProps} />
                  {label}
                </Label>
              </FormGroup>
            </Fragment>
          ),
          radio: () => (
            <FormGroup tag="fieldset">
              {label && <Label>{label}</Label>}
              <div className="form-inline">
                {
                  options.map(({ label, value: _value }) => (
                    <FormGroup check key={_value} className="mr-2">
                      <ReactstrapLabel check>
                        <Input name={fieldName} type="radio" checked={_value === value} onChange={_ => setValueAndStart(_value)} className={validationCss} disabled={readOnly(value, props)} {...inputProps} />
                        {label}
                      </ReactstrapLabel>
                    </FormGroup>
                  ))
                }
              </div>
            </FormGroup>
          ),
          select: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Select
                data-test={fieldName}
                value={options.find(_ => _.value === value) || null}
                onChange={_ => setValueAndStart(_ && _.value)}
                className={classnames('form-select', validationCss)}
                options={options}
                isDisabled={readOnly(value, props)}
                isClearable
                filterOption={(option, keyword) => (option.data?.queryLabel || [option.label, option.value].join(' ')).includes(keyword)}
                placeholder={placeholder}
                {...inputProps}
              />
            </Fragment>
          ),
          multiSelect: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Select
                data-test={fieldName}
                isMulti
                value={(value || []).map(v => options.find(_ => _.value === v)) || null}
                onChange={_ => setValueAndStart((_ || []).map(_ => _.value))}
                className={classnames('form-select', validationCss)}
                options={options}
                isDisabled={readOnly(value, props)}
                isClearable
                placeholder={placeholder}
                filterOption={createFilter({ stringify: _ => _.data.queryLabel || [_.label, _.value].join(' ')})}
                {...inputProps}
              />
            </Fragment>
          ),
          date: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <div>
                <DatePicker
                  data-test={fieldName}
                  dateFormat="yyyy/MM/dd"
                  timeIntervals={10}
                  selected={value}
                  onChange={setValueAndStart}
                  className={classnames('form-control', validationCss)}
                  readOnly={readOnly(value, props)}
                  placeholderText={placeholder}
                  isClearable
                  {...inputProps}
                />
              </div>
            </Fragment>
          ),
          time: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <div>
                <DatePicker
                  data-test={fieldName}
                  showTimeSelect
                  showTimeSelectOnly
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  selected={value}
                  onChange={_ => value != null ? setValueAndStart(setHours(setMinutes(value, getMinutes(_)), getHours(_))) : _}
                  className={classnames('form-control', validationCss)}
                  readOnly={readOnly(value, props)}
                  placeholder={placeholder}
                />
              </div>
            </Fragment>
          ),
          text: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input type="textarea" value={value} onChange={_ => setValueAndStart(_.target.value)} className={validationCss} readOnly={readOnly(value, props)} placeholder={placeholder} rows={rows} />
            </Fragment>
          ),
          files: () => (
            <Fragment>
              {label && <Label>{label}</Label>}
              <Input type="file" multiple name={fieldName} onChange={_ => setValueAndStart(get(_, 'target.files'))} className={validationCss} readOnly={readOnly(value, props)} {...inputProps} />
            </Fragment>
          ),
        })[type]()
      }
      {hint && <small className="form-text text-muted" style={{ whiteSpace: 'pre-line' }}>{hint}</small>}
      {
        hasStarted && type !== 'list' && validationErrors.length > 0 && (
          <div className="small text-danger mt-1">
            {
              validationErrors.map(k => (
                <div key={k}>{validationText(documentName, fieldName.replace(/\.\d+\./g, '.fields.'), k)}</div>
              ))
            }
          </div>
        )
      }
    </FormGroup>
  );
};

